import React, { useMemo } from "react";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import * as Yup from "yup";
import { omit } from "lodash";

import Modal from "Components/Common/Modal";
import InputFieldText from "Components/Common/InputFieldText";
import { DocumentName, UploadImage } from "pages/Admin/TechnicalSanction/style";
import { Text } from "Components/MeterInfoForm/ConsumerDetails/styles";

import getFileExtension from "utils/getFileExtension";
import { generateFormData, getImageUrl, onlyNumberKeys } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import {
  useGetConsumerBankAndPaymentDetails,
  usePostAddUserBankInfoMutation,
} from "apis/ApplicationRequestDetail";

import pdfImg from "assets/images/pdf.png";

const validFileFormats = ["jpg", "png", "jpeg", "pdf"];

const BankInformationFormModal = ({
  bankInformationModalProps: { utilityConsumerId, applicationId },
  handleClose,
  t,
}) => {
  const [addBankInformation, { isLoading }] = usePostAddUserBankInfoMutation();

  const { data: { data: { bankDetail, refundDetail } = {} } = {} } =
    useGetConsumerBankAndPaymentDetails(utilityConsumerId);

  const { utrNumber, amount, paymentDate } = refundDetail || {};
  const initialValues = useMemo(
    () => ({
      applicationId: applicationId ?? "",
      utilityConsumerId,
      beneficiaryName: bankDetail?.beneficiaryName ?? "",
      bankAccountNumber: bankDetail?.bankAccountNumber ?? "",
      branchName: bankDetail?.branchName ?? "",
      iFSCCode: bankDetail?.ifscCode ?? "",
      bankName: bankDetail?.bankName ?? "",
      cancelCheque: bankDetail?.cancelChequeUrl ?? "",
      cancelationCheckUrl: bankDetail?.cancelChequeUrl ?? "",
    }),
    [bankDetail]
  );
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      beneficiaryName: Yup.string().required("Beneficiary name is required"),
      bankAccountNumber: Yup.string().required("Account number is required"),
      branchName: Yup.string().required("Branch name is required"),
      iFSCCode: Yup.string()
        .required("IFSC code is required")
        .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Enter valid IFSC code"),
      bankName: Yup.string().required("bank name is required"),
      cancelationCheckUrl: "",
    }),
    onSubmit: async (values) => {
      if (!values.cancelCheque) {
        toast.error("Please upload cancelation check image");
      } else {
        try {
          const formData = new FormData();
          generateFormData(formData, omit(values, "cancelationCheckUrl"));
          await addBankInformation(formData);
          handleClose();
        } catch (error) {}
      }
    },
  });

  const {
    setFieldValue,
    handleSubmit,
    values: { cancelationCheckUrl, cancelCheque },
  } = formik;

  const fileChangeHandler = (e) => {
    const file = e.target.files[0];

    const isAllFileValid = validFileFormats.includes(
      getFileExtension(file.name.toLowerCase())
    );

    if (!isAllFileValid) {
      toast.error("Invalid file format, Please upload image or pdf  file");
    } else {
      setFieldValue("cancelCheque", file);
      setFieldValue("cancelationCheckUrl", URL.createObjectURL(file));
      e.target.value = "";
    }
  };

  return (
    <Modal
      isOpen
      title="Bank Information"
      handleClose={handleClose}
      maxWidth="md"
    >
      <Grid container columnSpacing={2} className="mt-2">
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            formik={formik}
            label={t("Beneficiary name") + "*"}
            name="beneficiaryName"
            placeholder="Enter Beneficiary name"
            type="text"
            disabled={!!bankDetail}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            formik={formik}
            label={t("Account Number") + "*"}
            name="bankAccountNumber"
            placeholder="Enter account number"
            type="number"
            InputProps={{
              inputProps: { min: 0, onKeyDown: onlyNumberKeys },
            }}
            disabled={!!bankDetail}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            formik={formik}
            label={t("Branch name") + "*"}
            name="branchName"
            placeholder="Enter Branch name"
            type="text"
            disabled={!!bankDetail}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            formik={formik}
            label={t("IFSC Code") + "*"}
            name="iFSCCode"
            placeholder="Enter IFSC code"
            type="text"
            disabled={!!bankDetail}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            formik={formik}
            label={t("Bank name") + "*"}
            name="bankName"
            placeholder="Enter Bank name"
            type="text"
            disabled={!!bankDetail}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6">{t("Cancellation Check")} :</Typography>
        </Grid>

        {cancelationCheckUrl ? (
          <div className="image-container ms-3 mt-1">
            <a
              href={
                bankDetail
                  ? getImageUrl(cancelationCheckUrl)
                  : cancelationCheckUrl
              }
              target="_blank"
              rel="noreferrer"
            >
              <Box
                component="img"
                className="image d-block m-auto"
                alt="document image"
                src={
                  getFileExtension(cancelationCheckUrl.toLowerCase()) === "pdf"
                    ? pdfImg
                    : bankDetail
                    ? getImageUrl(cancelationCheckUrl)
                    : cancelationCheckUrl
                }
              />
            </a>
            {!bankDetail && (
              <i
                className="ri-close-circle-line fs-3 close-btn"
                onClick={() => {
                  setFieldValue("cancelationCheckUrl", "");
                  setFieldValue("cancelCheque", "");
                }}
              />
            )}
            <Tooltip title={name}>
              <DocumentName fontSize={13} variant="body2">
                {cancelCheque.name}
              </DocumentName>
            </Tooltip>
          </div>
        ) : (
          <Box component="label" className="m-0 p-0 cursor-pointer ms-3 mt-1">
            <UploadImage isTechnicalFeasibility>
              <AddIcon
                sx={{
                  color: "black",
                }}
              />
            </UploadImage>
            <input
              type="file"
              accept="image pdf/*"
              onChange={fileChangeHandler}
              multiple
              hidden
            />
          </Box>
        )}

        {!!refundDetail && (
          <>
            <Grid item xs={12} className="mb-2 mt-2">
              <Typography variant="h6">{t("Payment Details")} :</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("UTR Number")}: </b>
                {utrNumber}
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Amount")}: </b>
                {amount}
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Payment Date: ")}: </b>
                {convertUTCDateToLocalDate(paymentDate).format("DD-MM-YYYY")}
              </Text>
            </Grid>
          </>
        )}
        <Grid xs={12}>
          <Typography color="error" className="mt-4 ms-3">
            {t(
              "*Once submitted, this information cannot be changed. So, confirm that all of the information is accurate."
            )}
          </Typography>
        </Grid>
      </Grid>
      {!bankDetail && (
        <Box className="d-flex justify-content-end">
          <Button
            size="medium"
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {t("Submit")} {isLoading && <CircularProgress size={20} />}
          </Button>
        </Box>
      )}
    </Modal>
  );
};

BankInformationFormModal.propTypes = {
  handleClose: PropTypes.func,
  bankInformationModalProps: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(BankInformationFormModal);
